/**
 * React surface: provider, boundary, fallback UI, reference-code badge.
 *
 * Every app mounts <ObservabilityProvider> once at the root layout,
 * passing its `app` name and (optionally) release. This:
 *   - calls initObservability
 *   - starts the client-side capture singleton
 *   - initializes breadcrumbs + auth listener
 *
 * Feature boundaries: wrap risky subtrees in <Boundary feature="X">
 * so a crash inside doesn't take down the whole page.
 */

'use client'

import { useEffect, type ErrorInfo, type ReactNode } from 'react'
import React from 'react'

import { clientLogger } from './client'
import { initObservability, type AppName, type Feature, type UserImpact } from './config'
import { generateTraceId, toReferenceCode } from './trace'

interface ProviderProps {
  app: AppName
  release?: string
  environment?: string
  defaultUserRole?: string
  mutedFingerprintPrefixes?: string[]
  children: ReactNode
}

/**
 * Mount once at the root layout. Initializes config + starts the
 * client capture singleton on mount. Idempotent — safe under Fast
 * Refresh + double-mount in dev.
 */
export function ObservabilityProvider({ children, ...cfg }: ProviderProps) {
  useEffect(() => {
    initObservability(cfg)
    clientLogger.init()
  }, [cfg])
  return <>{children}</>
}

// ─────────────────────────────────────────────────────────────────────
// Feature boundary
// ─────────────────────────────────────────────────────────────────────

interface BoundaryProps {
  feature: Feature
  fallback?: (props: { error: Error; reset: () => void; referenceCode: string }) => ReactNode
  userImpact?: UserImpact
  children: ReactNode
}

interface BoundaryState {
  error: Error | null
  traceId: string | null
}

/**
 * Feature-level error boundary. Catches renders throws inside its
 * subtree, logs with the feature tag, and shows a friendly fallback
 * with a reference code. The rest of the page keeps working.
 */
export class Boundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, traceId: null }

  static getDerivedStateFromError(error: Error): Partial<BoundaryState> {
    // Generate the traceId synchronously so the fallback UI can render
    // the reference code on the FIRST paint. Without this, users see
    // "----" briefly, then it flips to the real code — confusing when
    // they're trying to copy it.
    return { error, traceId: generateTraceId() }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    void clientLogger.error(error, {
      traceId: this.state.traceId || undefined,
      feature: this.props.feature,
      userImpact: this.props.userImpact || 'blocked',
      handled: true,
      componentName: 'FeatureBoundary',
      action: this.props.feature,
      metadata: { componentStack: info.componentStack },
    })
  }

  reset = () => this.setState({ error: null, traceId: null })

  render() {
    if (!this.state.error) return this.props.children
    const referenceCode = this.state.traceId ? toReferenceCode(this.state.traceId) : '----'
    if (this.props.fallback) {
      return this.props.fallback({ error: this.state.error, reset: this.reset, referenceCode })
    }
    return <SectionFallback featureName={this.props.feature} referenceCode={referenceCode} onRetry={this.reset} />
  }
}

// ─────────────────────────────────────────────────────────────────────
// Fallback UIs — user-facing surface for the four tiers
// ─────────────────────────────────────────────────────────────────────

/**
 * Tier 3 fallback — one feature crashed. Rest of the app is fine.
 */
export function SectionFallback({
  featureName,
  referenceCode,
  onRetry,
}: {
  featureName: string
  referenceCode: string
  onRetry?: () => void
}) {
  return (
    <div
      role="alert"
      style={{
        border: '1px solid #fca5a5',
        background: '#fef2f2',
        padding: 20,
        borderRadius: 8,
        margin: 16,
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 16, color: '#991b1b' }}>
        This section isn&apos;t loading right now
      </h3>
      <p style={{ margin: '8px 0 0', color: '#7f1d1d', fontSize: 14 }}>
        Refresh the page, or come back in a moment. If it keeps happening,
        contact support with the reference code below.
      </p>
      <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid #b91c1c',
              background: '#fff',
              color: '#991b1b',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Try again
          </button>
        )}
        <ReferenceCode code={referenceCode} />
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
        Feature: {featureName}
      </div>
    </div>
  )
}

/**
 * Tier 4 fallback — the whole app crashed. Used from app/global-error.tsx.
 */
export function AppFallback({
  referenceCode,
  onRetry,
  supportEmail = 'support@alli.io',
}: {
  referenceCode: string
  onRetry?: () => void
  supportEmail?: string
}) {
  return (
    <div style={{ margin: 0, fontFamily: 'system-ui, sans-serif', minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ maxWidth: 480, margin: '80px auto', padding: 24 }}>
        <h1 style={{ fontSize: 24, marginBottom: 12, color: '#111827' }}>Something went wrong</h1>
        <p style={{ color: '#4b5563', lineHeight: 1.5 }}>
          We hit an unexpected error. Please refresh the page. If it keeps
          happening, contact <a href={`mailto:${supportEmail}`}>{supportEmail}</a>{' '}
          with the reference code below.
        </p>
        <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
          {onRetry && (
            <button
              onClick={onRetry}
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                border: 'none',
                background: '#111',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 15,
              }}
            >
              Try again
            </button>
          )}
          <ReferenceCode code={referenceCode} />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Reference code badge — the AB12-CD34 shown to users
// ─────────────────────────────────────────────────────────────────────

export function ReferenceCode({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* ignore */
    }
  }
  return (
    <button
      onClick={copy}
      title="Click to copy"
      aria-label={`Reference code ${code}, click to copy`}
      style={{
        fontFamily: 'ui-monospace, monospace',
        fontSize: 13,
        padding: '4px 8px',
        borderRadius: 6,
        border: '1px solid #d1d5db',
        background: '#f3f4f6',
        color: '#374151',
        cursor: 'pointer',
      }}
    >
      {copied ? 'Copied ✓' : code}
    </button>
  )
}
